/**
 * ReviewQueue — REVIEW and PUBLISH (and therefore SUPERSEDE).
 *
 * The queue shows every state, not just `proposed`. A queue filtered to what is
 * waiting cannot tell an operator "nothing is waiting" from "everything was
 * rejected", and the second one is a fact about the pipeline they need.
 *
 * Selecting a proposal loads its SOURCE DOCUMENT, because reviewing a proposed
 * external requirement without seeing the document it was drawn from, who
 * ingested it, who verified its identity and AS WHAT, is a rubber stamp with a
 * name on it. The declared class and the verified class are shown separately
 * and neither substitutes for the other: a blog declared a federal regulation
 * is a blog.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ConsoleClient } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import * as requests from '../platform/requests.js';
import type {
  DocumentResponse,
  Requirement,
  RuleProposal,
  SourceClass,
  SourceDocument,
} from '../platform/types.js';
import {
  describePublicationEffect,
  describeSourceIdentity,
  describeVersionLabel,
  describeWindow,
  publicationAvailability,
  reviewAvailability,
} from '../platform/lifecycle.js';
import { Outcome } from './Outcome.js';
import type { ActResult } from './Outcome.js';

export function ReviewQueue({
  proposals,
  corpus,
  classes,
  actor,
  client,
  onChanged,
}: {
  proposals: RuleProposal[] | null;
  corpus: Requirement[] | null;
  classes: SourceClass[] | null;
  actor: PlatformActor;
  client: ConsoleClient;
  onChanged: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = proposals?.find((p) => p.id === selectedId) ?? null;

  return (
    <section>
      <h2>Review queue</h2>
      {proposals === null ? (
        <p className="quiet">
          WITHHELD — the queue did not load. This is not an empty queue; the console does not know
          what is in it.
        </p>
      ) : proposals.length === 0 ? (
        <p className="quiet">
          No proposals exist in any state. That is a measured zero across the whole table, not a
          filtered view.
        </p>
      ) : (
        <div className="row">
          <div className="col">
            {proposals.map((p) => (
              <button
                key={p.id}
                type="button"
                className="card"
                data-selected={p.id === selectedId}
                style={{ display: 'block', width: '100%', textAlign: 'left' }}
                onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
              >
                <h3>{p.title}</h3>
                <p className="quiet">
                  {p.state} · {p.subject} · {p.jurisdiction ?? 'no jurisdiction recorded'} ·{' '}
                  {p.bindingness}
                </p>
              </button>
            ))}
          </div>
          <div className="col">
            {selected ? (
              <ProposalDetail
                proposal={selected}
                corpus={corpus}
                classes={classes}
                actor={actor}
                client={client}
                onChanged={onChanged}
              />
            ) : (
              <p className="quiet">Select a proposal.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ProposalDetail({
  proposal,
  corpus,
  classes,
  actor,
  client,
  onChanged,
}: {
  proposal: RuleProposal;
  corpus: Requirement[] | null;
  classes: SourceClass[] | null;
  actor: PlatformActor;
  client: ConsoleClient;
  onChanged: () => void;
}) {
  const [document, setDocument] = useState<SourceDocument | null>(null);
  const [documentProblem, setDocumentProblem] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<ActResult | null>(null);

  useEffect(() => {
    let live = true;
    setDocument(null);
    setDocumentProblem(null);
    void client<DocumentResponse>(requests.document(proposal.documentId), actor).then((outcome) => {
      if (!live) return;
      if (outcome.kind === 'ok') setDocument(outcome.value.document);
      else setDocumentProblem(outcome.message);
    });
    return () => {
      live = false;
    };
  }, [client, actor, proposal.documentId]);

  const act = useCallback(
    async (request: requests.ConsoleRequest, label: string) => {
      setResult(null);
      const outcome = await client<unknown>(request, actor);
      setResult({ label, outcome });
      if (outcome.kind === 'ok') onChanged();
    },
    [client, actor, onChanged],
  );

  const review = reviewAvailability(proposal, actor.operatorId);
  const publish = publicationAvailability(proposal);
  // The class the DOCUMENT was verified as — not the class it was declared as,
  // and not a guess. Absent until verification, and rendered as absent.
  const verifiedClass = document?.verifiedSourceClass
    ? (classes?.find((c) => c.id === document.verifiedSourceClass) ?? null)
    : null;

  return (
    <div className="card">
      <h3>{proposal.title}</h3>
      <dl>
        <dt>Subject</dt>
        <dd>{proposal.subject}</dd>
        <dt>Applicability</dt>
        <dd>
          {[
            proposal.jurisdiction ? `jurisdiction ${proposal.jurisdiction}` : null,
            proposal.programKey ? `program ${proposal.programKey}` : null,
            proposal.serviceKey ? `service ${proposal.serviceKey}` : null,
            proposal.payerKey ? `payer ${proposal.payerKey}` : null,
            proposal.roleKey ? `role ${proposal.roleKey}` : null,
          ]
            .filter(Boolean)
            .join(' · ') || 'No applicability keys recorded — this proposal is unscoped.'}
        </dd>
        <dt>Effective dates</dt>
        <dd>{describeWindow(proposal.effectiveFrom, proposal.effectiveTo)}</dd>
        <dt>Version label</dt>
        <dd>{describeVersionLabel(proposal.versionLabel)}</dd>
        <dt>Bindingness</dt>
        <dd>{proposal.bindingness}</dd>
        <dt>Audit history</dt>
        <dd>
          Proposed by {proposal.proposedBy} on {proposal.proposedAt}.{' '}
          {proposal.reviewedBy
            ? `Reviewed by ${proposal.reviewedBy} on ${proposal.reviewedAt}${
                proposal.reviewNote ? `: ${proposal.reviewNote}` : ' with no note recorded.'
              }`
            : 'Not yet reviewed.'}
        </dd>
        <dt>Source</dt>
        <dd>
          {documentProblem ? (
            <span className="quiet">
              The source document could not be loaded: {documentProblem} Do not review this proposal
              without it.
            </span>
          ) : document ? (
            <>
              <div>
                {document.title} — {document.publisher}
              </div>
              <div className="quiet">
                {document.citation ?? 'No citation recorded.'}{' '}
                {document.referenceUrl ?? 'No reference URL recorded.'}
              </div>
              <div className="quiet">
                Ingested by {document.ingestedBy} on {document.ingestedAt}; retrieved{' '}
                {document.retrievedAt}; sha256 {document.contentSha256}
              </div>
              <div>{describeSourceIdentity(document)}</div>
              {verifiedClass ? (
                <div className={verifiedClass.eligibleAsFloor ? 'quiet' : 'banner'}>
                  {verifiedClass.label}:{' '}
                  {verifiedClass.eligibleAsFloor
                    ? `eligible as a floor, up to ${verifiedClass.maxBindingness}.`
                    : `INELIGIBLE — ${verifiedClass.ineligibilityReason ?? 'no reason recorded, which the catalog forbids'}`}
                </div>
              ) : (
                <div className="quiet">
                  The verified source class is not resolvable from the catalog this console loaded.
                  Its eligibility is unknown here — the publication path will still enforce it.
                </div>
              )}
            </>
          ) : (
            <span className="quiet">Loading the source document…</span>
          )}
        </dd>
        <dt>Rule text</dt>
        <dd>
          <pre>{proposal.ruleText}</pre>
        </dd>
        <dt>Structured rule</dt>
        <dd>
          {proposal.structuredRule ? (
            <pre>{JSON.stringify(proposal.structuredRule, null, 2)}</pre>
          ) : (
            <span className="quiet">
              None extracted. The requirement is text only; nothing will compare against it
              structurally.
            </span>
          )}
        </dd>
        <dt>What publishing will do</dt>
        <dd>
          {corpus === null
            ? 'The corpus did not load, so the console cannot say what this would supersede. Do not publish blind.'
            : describePublicationEffect(proposal, corpus)}
        </dd>
      </dl>

      <label htmlFor="note">Finding (required to reject; recorded either way)</label>
      <textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />

      <p className="row">
        <button
          type="button"
          disabled={!review.available}
          onClick={() => void act(requests.review(proposal.id, 'approved', note), 'Approve')}
        >
          Approve
        </button>
        <button
          type="button"
          disabled={!review.available || !note.trim()}
          onClick={() => void act(requests.review(proposal.id, 'rejected', note), 'Reject')}
        >
          Reject
        </button>
        <button
          type="button"
          disabled={!publish.available}
          onClick={() => void act(requests.publish(proposal.id), 'Publish')}
        >
          Publish{proposal.supersedesRuleId ? ' (supersedes)' : ''}
        </button>
      </p>
      {review.available ? null : <p className="quiet">Review: {review.because}</p>}
      {publish.available ? null : <p className="quiet">Publish: {publish.because}</p>}
      {review.available && !note.trim() ? (
        <p className="quiet">
          Reject is disabled until a finding is written. A rejection without the reason a reviewer
          gave is refused by the door, and it would be worthless in the audit trail anyway.
        </p>
      ) : null}

      <Outcome result={result} />
    </div>
  );
}
